import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../Services/AuthContext';
import { apiFetch } from "../utils/api";
import { formatFriendlyCurrency, formatZARCurrency } from "../utils/formatUtils";
import {
  ShoppingCart,
  CreditCard,
  FileText,
  Megaphone,
  Target,
  BarChart3,
  ExternalLink,
  Star,
  ArrowRight,
  Plus,
  Eye,
  Clock,
  Download,
  CheckCircle,
  XCircle,
  Search
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';

export function Dashboard({ setActiveSection, navigateToCreateOpportunity, navigateToRenewals, navigateToAnnouncement }) {

  const { login, user, token, idToken, currentPartner, appUser } = useAuth();
  const [salesData, setSalesData] = useState([]);

  const fetchMetrics = async () => {
    console.log(`fetching metrics for ${currentPartner?.partner_id}`)
    if (currentPartner && currentPartner.partner_id) {
      const res = apiFetch(await idToken(), `/api/metrics?partner_id=${currentPartner.partner_id}&company_id=${currentPartner.company_id}`);
      res.then(async (metrics) => {

        // let metrics = invoicess_response.data.map(item => {
        //   const d = new Date(item.data_month);
        //   const monthName = d.toLocaleDateString('en-US', { month: 'long' });

        //   return {
        //     ...item,
        //     month: monthName
        //   };
        // });

        setSalesData(metrics.data);
      });
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [currentPartner]);

  useEffect(() => {
    if (currentPartner && currentPartner.partner_id) {
      fetchMetrics();
    }
  }, []);


  return (
    <div className="space-y-6 animate-fade-in-fast">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>Welcome back, {appUser?.first_name}!</h1>
            <p className="text-muted-foreground mt-2">
              Here's what's happening with your partner network today.
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Today</p>
            <p className="font-medium">{new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</p>
          </div>
        </div>
      </div>

      {/* Dashboard Grid - Improved Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Top Row */}
        <div className="lg:col-span-3">
          <QuickActionsTile setActiveSection={setActiveSection} navigateToCreateOpportunity={navigateToCreateOpportunity} navigateToRenewals={navigateToRenewals} />
        </div>

        <div className="lg:col-span-3">
          <AnnouncementsTile setActiveSection={setActiveSection} navigateToAnnouncement={navigateToAnnouncement} />
        </div>

        <div className="lg:col-span-3">
          <RecentContentTile setActiveSection={setActiveSection} />
        </div>

        <div className="lg:col-span-3">
          <ExternalLinksTile setActiveSection={setActiveSection} />
        </div>

        {/* Middle Row - OpportunitiesTile Full Width */}
        <div className="lg:col-span-12">
          {currentPartner &&
            <OpportunitiesTile setActiveSection={setActiveSection} />
          }
        </div>

        <div className="lg:col-span-12 min-h-[400px]">
          {currentPartner &&
            <SalesMetricsTile salesData={salesData} />
          }
        </div>
      </div>
    </div>
  );
}


function QuickActionsTile({ setActiveSection, navigateToCreateOpportunity, navigateToRenewals }: { setActiveSection: (section: string) => void; navigateToCreateOpportunity?: () => void; navigateToRenewals?: () => void }) {
  const quickActions = [
    {
      id: 1,
      title: 'Create Opportunity',
      description: 'Start a new sales opportunity',
      icon: Plus,
      color: 'text-worldsview-green',
      onClick: () => navigateToCreateOpportunity ? navigateToCreateOpportunity() : setActiveSection('Sales & Renewals')
    },
    {
      id: 2,
      title: 'Price Look up',
      description: 'Search and view product pricing',
      icon: Search,
      color: 'text-worldsview-orange',
      onClick: () => setActiveSection('Price Lookup')
    },
    {
      id: 3,
      title: 'View All Opportunities',
      description: 'Browse all sales opportunities',
      icon: Eye,
      color: 'text-worldsview-blue',
      onClick: () => setActiveSection('Sales & Renewals')
    },
    {
      id: 4,
      title: 'View Open Renewals',
      description: 'Check pending renewals',
      icon: Clock,
      color: 'text-worldsview-orange',
      onClick: () => navigateToRenewals ? navigateToRenewals() : setActiveSection('Sales & Renewals')
    }
  ];

  return (
    <Card className="glass-card h-full flex flex-col">
      <CardHeader className="flex-shrink-0 pb-3">
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-worldsview-purple" />
          Quick Actions
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {quickActions.map((action) => {
          const IconComponent = action.icon;
          return (
            <Button
              key={action.id}
              variant="ghost"
              className="w-full justify-start glass-button hover-glass p-3 h-auto flex-col items-start"
              onClick={action.onClick}
            >
              <div className="flex items-center gap-3 w-full mb-1">
                <div className={`w-8 h-8 rounded-lg ${action.color.replace('text-', 'bg-')}/20 flex items-center justify-center flex-shrink-0`}>
                  <IconComponent className={`w-4 h-4 ${action.color}`} />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{action.title}</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-left w-full pl-11 break-words line-clamp-2">
                {action.description}
              </p>
            </Button>
          );
        })}
      </CardContent>
    </Card>
  );
}

function OpportunitiesTile({ setActiveSection }) {

  const { idToken, currentPartner } = useAuth();
  const [overviewData, setOverviewData] = useState([]);

  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [currentPartner]);

  const fetchMetrics = async () => {
    if (currentPartner) {
      let tk = await idToken();
      const res = apiFetch(tk, `/api/metrics/opportunities_overview?partner_id=${currentPartner.partner_id}`);
      res.then(async (response) => {
        setOverviewData(response.data);
      });
    }
  };

  const handleStatusClick = (filter: string) => {
    // Navigate to sales-renewals with filter parameter
    // For now, just navigate to the section - the filter logic would be implemented in SalesRenewals component
    setActiveSection('sales-renewals');
  };

  return (
    <Card className="glass-card h-full flex flex-col">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-worldsview-orange" />
            Opportunities Overview
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Open */}
          <Button
            key={`open`}
            variant="ghost"
            className="p-4 glass-input rounded-lg hover-glass h-auto flex-col  group"
          >
            <div className="w-full space-y-3">
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-lg bg-worldsview-blue/20 flex items-center justify-center flex-shrink-0`}>
                  <Target className={`w-5 h-5 text-worldsview-blue`} />
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold text-worldsview-blue`}>{overviewData?.open?.length}</div>
                  <div className="text-xs text-muted-foreground">Opportunities</div>
                </div>
              </div>
              <div className="text-left w-full">
                <h4 className="font-medium text-sm mb-1">Open</h4>
                <p className="text-xs text-muted-foreground mb-2">Active opportunities</p>
                <p className="text-sm font-medium text-worldsview-orange mb-2">
                  {formatZARCurrency(overviewData?.open?.reduce((sum, item) => {
                    return sum + Number(item.opportunity_value || 0);
                  }, 0))}
                </p>
                {/* Breakdown by driver type */}
                <div className="space-y-1 pt-2 border-t border-border/50">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">New Seats:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {overviewData?.open?.filter((it) => it.driver_type != 'Renewals').length}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-worldsview-green">
                        {formatZARCurrency(overviewData?.open?.filter((it) => it.driver_type != 'Renewals').reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0))}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Renewals:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {overviewData?.open?.filter((it) => it.driver_type == 'Renewals').length}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-worldsview-blue">
                        {formatZARCurrency(overviewData?.open?.filter((it) => it.driver_type == 'Renewals').reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Button>

          {/* Closed */}
          <Button
            key={`closed`}
            variant="ghost"
            className="p-4 glass-input rounded-lg hover-glass h-auto flex-col group"
          >
            <div className="w-full space-y-3">
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-lg bg-worldsview-green/20 flex items-center justify-center flex-shrink-0`}>
                  <CheckCircle className={`w-5 h-5 text-worldsview-green`} />
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold text-worldsview-green`}>{overviewData?.won?.length}</div>
                  <div className="text-xs text-muted-foreground">Opportunities</div>
                </div>
              </div>
              <div className="text-left w-full">
                <h4 className="font-medium text-sm mb-1">Closed</h4>
                <p className="text-xs text-muted-foreground mb-2">Won opportunities</p>
                <p className="text-sm font-medium text-worldsview-orange mb-2">
                  {formatZARCurrency(overviewData?.won?.reduce((sum, item) => {
                    return sum + Number(item.opportunity_value || 0);
                  }, 0))}
                </p>
                {/* Breakdown by driver type */}
                <div className="space-y-1 pt-2 border-t border-border/50">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">New Seats:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {overviewData?.won?.filter((it) => it.driver_type != 'Renewals').length}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-worldsview-green">
                        {formatZARCurrency(overviewData?.won?.filter((it) => it.driver_type != 'Renewals').reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0))}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Renewals:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {overviewData?.won?.filter((it) => it.driver_type == 'Renewals').length}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-worldsview-blue">
                        {formatZARCurrency(overviewData?.won?.filter((it) => it.driver_type == 'Renewals').reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Button>

          {/* Lost */}
          <Button
            key={`closed`}
            variant="ghost"
            className="p-4 glass-input rounded-lg hover-glass h-auto flex-col group"
          >
            <div className="w-full space-y-3">
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-lg bg-worldsview-red/20 flex items-center justify-center flex-shrink-0`}>
                  <XCircle className={`w-5 h-5 text-worldsview-red`} />
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold text-worldsview-red`}>{overviewData?.lost?.length}</div>
                  <div className="text-xs text-muted-foreground">Opportunities</div>
                </div>
              </div>
              <div className="text-left w-full">
                <h4 className="font-medium text-sm mb-1">Lost</h4>
                <p className="text-xs text-muted-foreground mb-2">Lost opportunities</p>
                <p className="text-sm font-medium text-worldsview-orange mb-2">
                  {formatZARCurrency(overviewData?.lost?.reduce((sum, item) => {
                    return sum + Number(item.opportunity_value || 0);
                  }, 0))}
                </p>
                {/* Breakdown by driver type */}
                <div className="space-y-1 pt-2 border-t border-border/50">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">New Seats:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {overviewData?.lost?.filter((it) => it.driver_type != 'Renewals').length}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-worldsview-green">
                        {formatZARCurrency(overviewData?.lost?.filter((it) => it.driver_type != 'Renewals').reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0))}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Renewals:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {overviewData?.lost?.filter((it) => it.driver_type == 'Renewals').length}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-medium text-worldsview-blue">
                        {formatZARCurrency(overviewData?.lost?.filter((it) => it.driver_type == 'Renewals').reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Button>

        </div>

        {/* Summary Stats */}
        <div className="mt-6 pt-4 border-t border-border/50">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-worldsview-purple">{overviewData?.open?.length + overviewData?.won?.length + overviewData?.lost?.length}</div>
              <div className="text-xs text-muted-foreground">Total Opportunities</div>
            </div>
            <div>
              <div className="text-lg font-bold text-worldsview-orange">
                {formatZARCurrency(
                  overviewData?.open?.reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0) 
                  + overviewData?.won?.reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0)
                  + overviewData?.lost?.reduce((sum, item) => {
                          return sum + Number(item.opportunity_value || 0);
                        }, 0))
                }
                </div>
              <div className="text-xs text-muted-foreground">Total Pipeline Value</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SalesMetricsTile({ salesData }) {
   
  return (
    <div className="grid grid-cols-2 gap-4 h-full">
      <Card className="glass-card h-full flex flex-col">
        <CardHeader className="flex-shrink-0 pb-3">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-worldsview-green" />
            New Seat Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-worldsview-green">
                {salesData && formatZARCurrency(salesData.ytd_new)}
              </div>
              <div className="text-xs text-muted-foreground">YTD New Seats</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-worldsview-blue">
                {salesData && salesData.new && formatZARCurrency(salesData.new[salesData.new.length - 1]?.value)}
              </div>
              <div className="text-xs text-muted-foreground">New Seats this Month</div>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesData.new}
                margin={{
                  top: 15
                }}>
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  fontSize={11}
                />
                <YAxis hide />
                <Bar
                  dataKey="value"
                  fill="var(--worldsview-green)"
                  label={{
                    position: 'top',
                    fontSize: 11,
                    fill: '#888',
                    formatter: (value: number) => `${formatZARCurrency(value)}`
                  }}
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card h-full flex flex-col">
        <CardHeader className="flex-shrink-0 pb-3">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-worldsview-blue" />
            Renewal Sales Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-worldsview-orange">
                {salesData && formatZARCurrency(salesData.ytd_renewal)}
              </div>
              <div className="text-xs text-muted-foreground">YTD Renewal Sales</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-worldsview-blue">
                {salesData && salesData.new && formatZARCurrency(salesData.renew[salesData.renew.length - 1]?.value)}
              </div>
              <div className="text-xs text-muted-foreground">Renewals This Month</div>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesData.renew}
                margin={{
                  top: 15
                }}>
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  fontSize={11}
                />
                <YAxis hide />
                <Bar
                  dataKey="value"
                  fill="var(--worldsview-blue)"
                  radius={[2, 2, 0, 0]}
                  label={{
                    position: 'top',
                    fontSize: 11,
                    fill: '#888',
                    formatter: (value: number) => `${formatZARCurrency(value)}`
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ExternalLinksTile({ setActiveSection }) {

  const [externalLinks, setExternalLinks] = useState([]);
  const { idToken, currentPartner } = useAuth();

  const fetchLinks = async () => {
    if (currentPartner && currentPartner.partner_id) {
      const res = apiFetch(await idToken(), `/api/content/dashboard_content?partner_id=${currentPartner.partner_id}`);
      res.then(async (response) => {
        setExternalLinks(response.data);
      });
    }
  };

  useEffect(() => {
    if (currentPartner && currentPartner.partner_id) {
      fetchLinks();
    }
  }, []);

  useEffect(() => {
    if (currentPartner && currentPartner.partner_id) {
      fetchLinks();
    }
  }, [currentPartner]);

  return (
    <Card className="glass-card h-full flex flex-col">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-worldsview-red" />
            Quick Links
          </CardTitle>

        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto space-y-2">
        {externalLinks.map((link) => (
          <Button
            key={link.id}
            variant="ghost"
            className="w-full justify-start glass-button hover-glass p-3 h-auto flex-col items-start"
            onClick={() => window.open(link.content_url, '_blank')}
          >
            <div className="flex items-center gap-2 w-full mb-1">
              <ExternalLink className="w-4 h-4 text-worldsview-red flex-shrink-0" />
              <div className="text-left flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{link.title}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-left w-full pl-6 break-words line-clamp-2">
              {link.description}
            </p>
          </Button>
        ))}
      </CardContent>
    </Card>
  );
}

function AnnouncementsTile({ setActiveSection, navigateToAnnouncement }) {
  const [notifications, setNotifications] = useState([]);
  const { idToken } = useAuth();

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, `/api/announcements?size=4`);
    res.then(async (response) => {
      setNotifications(response.data);
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-worldsview-red';
      case 'medium': return 'bg-worldsview-orange';
      case 'low': return 'bg-worldsview-blue';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Card className="glass-card h-full flex flex-col">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-worldsview-orange" />
            Announcements
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveSection('Announcements')}
            className="glass-button"
          >
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto space-y-3">
        {notifications.length == 0 &&
          <div>No announcements</div>
        }
        {notifications.map((announcement) => (
          <div 
            key={announcement.id} 
            className="p-3 glass-input rounded-lg hover-glass cursor-pointer"
            onClick={() => navigateToAnnouncement && navigateToAnnouncement(announcement.id)}
          >
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-medium truncate flex-1">{announcement.announcement.title}</h4>
              <Badge className={`text-white text-xs flex-shrink-0 ${getPriorityColor(announcement.announcement.priority)}`}>
                {announcement.announcement?.priority?.toUpperCase()}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{announcement.announcement.content.length > 90
              ? announcement.announcement.content.substring(0, 90) + '...'
              : announcement.announcement.content}</p>
            <p className="text-xs text-muted-foreground">{announcement.announcement.publish_date}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RecentContentTile({ setActiveSection }: { setActiveSection: (section: string) => void }) {

  const { login, user, token, idToken, currentPartner, setCurrentPartner, appUser, logout } = useAuth();

  const [content, setContent] = useState([]);
  const fetchContent = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, `/api/content?global=false&size=4`);
    res.then(async (response) => {
      setContent(response.data);
    });
  };

  useEffect(() => {
    fetchContent();
  }, []);

  return (
    <Card className="glass-card h-full flex flex-col">
      <CardHeader className="flex-shrink-0 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-worldsview-green" />
            New Content
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveSection('Information Hub')}
            className="glass-button"
          >
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto space-y-3">
        {content.map((content) => (
          <div key={content.id} className="p-3 glass-input rounded-lg hover-glass">
            <h4 className="text-sm font-medium mb-1 line-clamp-1">{content.title}</h4>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">{content.content_folder.name}</p>
              <p className="text-xs text-muted-foreground">{content.publish_date}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}